<template>
  <header>
    <env-bar />
    <div class="header-container">
      <div class="oringal-header">
        <div class="left-menu" @click="top()">
          <svg-logo class="logo" color="white" />
        </div>
        <nav>
          <ul>
            <li v-if="usePlaceGroupMenu" @click="showPlaceGroupMenu = !showPlaceGroupMenu">
              <dropdown-menu
                v-if="showPlaceGroupMenu"
                class="dropdown place-group-dropdown"
                @hide="showPlaceGroupMenu = false"
              >
                <ul>
                  <li
                    v-for="placeGroup in placeGroupList"
                    :key="placeGroup.placeGroupIdHash"
                    class="clickable"
                    @click="$emit('select-place-group', placeGroup)"
                  >
                    {{ placeGroup.placeGroupName }}
                  </li>
                </ul>
              </dropdown-menu>
              <div class="icon-wrapper clickable">
                <span>{{ selectingPlaceGroup !== undefined ? selectingPlaceGroup.placeGroupName : "-" }}</span>
                <icon class="hide-xs" name="expand_more" />
              </div>
            </li>
            <li v-if="usePlacePulldownMenu" @keydown.escape="showPlacePulldownMenu = false">
              <button type="button" class="header-button" @click="showPlacePulldownMenu = !showPlacePulldownMenu">
                <span class="icon-wrapper clickable">
                  <span>{{ showUsePlacePulldownTitle }}</span>
                  <icon class="hide-xs" name="expand_more" />
                </span>
              </button>
              <dropdown-menu
                v-if="showPlacePulldownMenu"
                class="dropdown place-dropdown"
                :padding-top="0"
                @hide="showPlacePulldownMenu = false"
              >
                <div class="search-place">
                  <div class="inner">
                    <icon class="search-xs" name="search" />
                    <label>
                      <span class="sr-only">place名称を入力して検索</span>
                      <input
                        v-model="searchedPlaceName"
                        type="text"
                        :placeholder="$t('Launch.header.placeSearch.placeHolder')"
                        @click.self.stop="(e) => e.preventDefault()"
                        @keyup.enter="searchPlace()"
                      />
                    </label>
                  </div>
                </div>
                <div v-if="showSearchError" class="network-error header-notice">
                  <icon :size="36" name="warning_amber" class="alert" />
                  <p>
                    {{ $t("Launch.header.placeSearch.error") }}<br />
                    {{ $t("Launch.header.placeSearch.checkEnvRetry") }}
                  </p>
                </div>
                <div v-else-if="!authorizedPlaceList.length" class="search-not-found header-notice">
                  <icon :size="36" name="info_outline" />
                  <p>
                    {{ $t("Launch.header.placeSearch.notFound") }}
                  </p>
                </div>
                <ul v-else>
                  <li v-for="place in authorizedPlaceList" :key="place.placeIdHash" class="clickable">
                    <button
                      type="button"
                      class="place-button"
                      @click="selectPlace(place)"
                      @keydown.enter="selectPlace(place)"
                      @keydown.space="selectPlace(place)"
                    >
                      <span v-cutTitle="{ name: 'place-title', title: place.name }" class="title">{{
                        place.name
                      }}</span>
                      <span v-if="place.address" class="address-wrapper">
                        <icon name="room" :size="12" />
                        <span v-cutTitle="{ name: 'place-address', title: place.address }" class="address">{{
                          place.address
                        }}</span>
                      </span>
                    </button>
                  </li>
                </ul>
                <div v-if="showSearchedPlaceHasMore" class="has-more-place">
                  <p>
                    {{ $t("Launch.header.placeSearch.overLimit") }}<br />
                    {{ $t("Launch.header.placeSearch.changeAndRetry") }}
                  </p>
                </div>
              </dropdown-menu>
            </li>
            <!-- user list -->
            <li @keydown.escape="showUserDropdownMenu = false">
              <button
                type="button"
                class="header-button"
                aria-label="アカウントメニュー"
                @click="showUserDropdownMenu = !showUserDropdownMenu"
              >
                <span class="icon-wrapper clickable">
                  <icon name="account_circle" />
                  <icon class="hide-xs" name="expand_more" />
                </span>
              </button>
              <dropdown-menu v-if="showUserDropdownMenu" class="dropdown" @hide="showUserDropdownMenu = false">
                <ul class="user-list">
                  <li class="user-name">
                    <button type="button" disabled>
                      {{ userNameMultipleRow(userName) }}
                    </button>
                  </li>
                  <li class="clickable lang-btn-hint">
                    <button
                      class="button"
                      @click="languageClicked"
                      @keydown.enter="languageClicked"
                      @keydown.space="languageClicked"
                    >
                      <span>Language</span>
                      <span>※一部サービスのみ対象</span>
                    </button>
                  </li>

                  <li class="clickable">
                    <button
                      type="button"
                      @keydown.enter="showWcagDialog"
                      @keydown.space="showWcagDialog"
                      @click="showWcagDialog"
                    >
                      {{ $t("userList.colorContrast.title") }}
                    </button>
                  </li>

                  <li class="clickable">
                    <button
                      type="button"
                      class="logout-btn"
                      @click="logout"
                      @keydown.enter="logout"
                      @keydown.space="logout"
                    >
                      {{ $t("userList.logout") }}
                    </button>
                  </li>
                </ul>
              </dropdown-menu>

              <language-promp
                v-if="showLanguagePromp"
                @select-and-close-view="selectAndCloseLanguagePromp"
                @close-view="closeLanguagePromp"
              />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { DirectiveBinding } from "vue/types/options";
import { mapMutations } from "vuex";
import Icon from "@/components/v/icon.vue";
import DropdownMenu from "@/components/v/dropdown.vue";
import SvgLogo from "@/components/svg-logo.vue";
import LanguagePromp from "@/components/language-prompt.vue";
import { signout } from "@/api/console.adapter.api";
import { decode } from "jsonwebtoken";
import EnvBar from "@/components/common/env.bar.vue";
import { RequestFetchHeaderResult } from "../../store/auth-module";
import { setLocale } from "@/utils/i18n";
import { removeMigrationStatus } from "@/api/migration/migration-v2.api";

type PlaceGroup = { placeGroupIdHash: string; placeGroupName: string };

export default Vue.extend({
  name: "component-place-header",
  components: {
    Icon,
    DropdownMenu,
    SvgLogo,
    LanguagePromp,
    EnvBar,
  },
  directives: {
    cutTitle: {
      bind(el: HTMLElement, binding: DirectiveBinding) {
        const btnName: string = binding.value.name;
        const title: undefined | string = binding.value.title;
        if (title === undefined) return;
        if (btnName === "place-title") {
          if (title.length > 22) {
            const shortTitle: string | undefined = title?.substring(0, 21);
            const newTitle: string | undefined = shortTitle?.concat("...");
            if (!newTitle) return;
            el.innerText = newTitle;
          } else {
            el.innerText = title;
          }
        } else if (btnName === "place-address") {
          if (title.length > 27) {
            const shortTitle: string | undefined = title?.substring(0, 26);
            const newTitle: string | undefined = shortTitle?.concat("...");
            if (!newTitle) return;
            el.textContent = newTitle;
          } else {
            el.textContent = title;
          }
        }
      },
    },
  },
  props: {
    usePlaceGroupMenu: Boolean,
    usePlacePulldownMenu: Boolean,
    placeGroupList: Array as PropType<PlaceGroup[]>,
    selectingPlaceGroup: Object as PropType<PlaceGroup>,
  },
  data(): {
    showLanguagePromp: boolean;
    showShopDropdownMenu: boolean;
    showUserDropdownMenu: boolean;
    showPlaceGroupMenu: boolean;
    showPlacePulldownMenu: boolean;
    searchedPlaceName: string;
    showSearchedPlaceEmpty: boolean;
    showSearchedPlaceHasMore: boolean;
    showSearchError: boolean;
  } {
    return {
      showLanguagePromp: false,
      showShopDropdownMenu: false as boolean,
      showUserDropdownMenu: false as boolean,
      showPlaceGroupMenu: false as boolean,
      showPlacePulldownMenu: false as boolean,
      searchedPlaceName: "",
      showSearchedPlaceEmpty: false as boolean,
      showSearchedPlaceHasMore: false as boolean,
      showSearchError: false as boolean,
    };
  },
  computed: {
    showUsePlacePulldownTitle(): string {
      return this.$route.path.includes("/launch")
        ? (this.$i18n.t("Launch.header.placeTitle") as string)
        : this.selectingPlace
        ? this.selectingPlace.name
        : "-";
    },
    userName(): string {
      const decodeToken: any = decode(localStorage.getItem("session/tokens/accessToken") || "");
      return decodeToken.username || "";
    },
    authorizedPlaceList(): any[] {
      return this.$store.state.auth.placeList;
    },
    selectingPlace(): any {
      return this.$store.state.auth.selectingPlace;
    },
  },
  watch: {
    usePlacePulldownMenu: {
      handler(newValue: boolean) {
        if (newValue) {
          this.$store.dispatch("auth/fetchHeaderAuthorizedPlaceList", this.searchedPlaceName).then((result) => {
            this.showSearchedPlaceHasMore = result.hasMore;
            this.showSearchError = result.status >= 400;
          });
        }
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch("auth/verifySelectedPlace");
  },
  methods: {
    ...mapMutations("menu", ["setWcagDialogShown"]),
    userNameMultipleRow(str: string): string {
      return str.length > 80 ? `${str.slice(0, 75)}...` : str;
    },
    askClicked() {
      window.open("https://support.vacancorp.com/hc/ja/requests/new");
    },
    languageClicked() {
      this.showLanguagePromp = true;
      // to focus language list
      setTimeout(() => {
        this.showUserDropdownMenu = false;
      }, 5);
    },
    closeLanguagePromp() {
      this.showLanguagePromp = false;
    },
    selectAndCloseLanguagePromp() {
      this.showLanguagePromp = false;
      setTimeout(() => {
        this.showUserDropdownMenu = false;
      }, 5);
      // select a language
      setLocale(this.$i18n.locale);
    },
    // go to page 待ち時間履歴
    top() {
      this.$router.push("/places");
    },
    async logout() {
      signout()
        .then(() => {
          localStorage.setItem("session/tokens/accessToken", "");
          localStorage.setItem("session/tokens/refreshToken", "");
          this.clearSessionStorage("spaces/placeId");
          this.clearSessionStorage("spaces/layoutId");
          removeMigrationStatus();
          this.clearVuexData();
          window.location.href = "/login";
        })
        .catch((e) => {
          localStorage.setItem("session/tokens/accessToken", "");
          localStorage.setItem("session/tokens/refreshToken", "");
          removeMigrationStatus();
          window.location.href = "/login";
        });
    },
    clearSessionStorage(keyName: string) {
      if ("sessionStorage" in window) {
        sessionStorage.removeItem(keyName);
      } else {
        console.error("session storage is not supported.");
      }
    },
    clearVuexData() {
      this.$store.commit("RESET");
    },
    selectPlace(place: any) {
      this.showPlacePulldownMenu = false;
      this.$store.commit("auth/setSelectingPlace", place);
      this.$emit("select-place");
    },
    async searchPlace() {
      const result: RequestFetchHeaderResult = await this.$store.dispatch(
        "auth/fetchHeaderAuthorizedPlaceList",
        this.searchedPlaceName,
      );

      this.showSearchedPlaceHasMore = result.hasMore;
      this.showSearchError = result.status >= 400;
    },

    showWcagDialog() {
      this.setWcagDialogShown(true);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "~@vacancorp/vacan-scss/mixins/media-query.scss";
@import "~@vacancorp/vacan-scss/mixins/shadows.scss";
@import "~@vacancorp/vacan-scss/variables/colors.scss";
@import "@/assets/colors.scss";
@import "~@vacancorp/vacan-scss/wcag.scss";

hr {
  height: 0;
  width: 88%;
  margin: 8px;
  border-top: 1px solid $north-snow;
}

ul {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  li {
    display: flex;
    align-items: center;
  }
}

header {
  display: flex;
  flex-direction: column;
  z-index: 9997;
  position: fixed;
  width: 100%;
  height: $header-height;
  box-shadow: 0 2px 4px rgb(0 0 0 / 40%);
  @include media-query(xs) {
    height: $header-height-short;
  }
  @include media-query(sm) {
    height: $header-height-short;
  }

  .header-container {
    width: 100%;
    z-index: $z-index-header;

    .oringal-header {
      display: flex;
      flex-flow: row;
      justify-content: space-between;
      align-items: center;
      background-color: $vacan;
      height: $original-header-height;
      @include media-query(xs) {
        height: $original-header-height-short;
      }
      @include media-query(sm) {
        height: $original-header-height-short;
      }
      .left-menu {
        display: flex;
        align-items: center;
        height: 30px;
        margin: 8px;
        color: $trooper;
        .logo {
          width: 120px;
          height: 100%;
          margin-left: 16px;
        }
      }

      nav {
        height: 100%;
        ul {
          height: 100%;
          color: $trooper;
          .hide-xs {
            @include media-query(xs) {
              display: none;
            }
          }

          li {
            height: 100%;
            margin: 0 8px;
            .icon-wrapper {
              display: flex;
              align-items: center;
              height: 100%;
            }

            nav.dropdown {
              height: auto;
              right: 16px;
              top: calc(#{$header-height} - 8px);
              max-height: calc(95vh - #{$header-height});

              @include media-query(xs) {
                height: 100vh;
              }

              @include media-query(sm) {
                top: $header-height-short;
              }

              @include media-query(xs) {
                right: 0;
                top: $header-height-short;
                max-height: calc(100vh - #{$header-height-short});
                width: 100%;
              }

              ul {
                height: 100%;
                flex-direction: column;
                color: $text;
                li {
                  min-height: 44px;
                  width: 100%;
                  margin: 0;
                  box-sizing: border-box;
                  &.clickable:hover {
                    background-color: $lavender;
                  }
                }
              }

              ul.user-list {
                width: 299px;
                height: auto;
                flex-direction: column;
                color: $text;
                display: flex;
                justify-content: space-evenly;

                @include media-query(xs) {
                  width: 100%;
                }

                li {
                  height: 52px;
                  width: 100%;
                  margin: 0;
                  box-sizing: border-box;
                  font-size: 12px;
                  line-height: 14px;
                  color: $text;
                  padding-left: 16px;

                  &.clickable:hover {
                    background-color: $lavender;
                  }

                  &.lang-btn-hint {
                    font-size: 10px;
                    line-height: 12px;
                    color: var(--spooky-ghost);
                    margin: 0;

                    span:first-child {
                      font-size: 12px;
                      line-height: 18px;
                      color: $text;
                      margin-right: 5px;
                    }
                  }

                  &.user-name {
                    font-size: 12px;
                    line-height: 18px;
                    color: $text;
                    width: 100%;
                    height: 44px;
                    word-break: break-all;
                  }

                  button {
                    width: calc(100% - 16px);
                    height: 52px;
                    border-bottom: 1px solid $north-snow;
                    background-color: transparent;
                    text-align: left;
                    outline: revert;

                    &:disabled {
                      cursor: not-allowed;
                      color: $text;
                    }

                    &.logout-btn {
                      border-bottom: 0;
                    }
                  }
                }
              }

              &.place-group-dropdown,
              &.place-dropdown {
                right: 80px;

                @include media-query(sm) {
                  top: $header-height-short;
                }

                @include media-query(xs) {
                  right: 0;
                  top: $header-height-short;
                  max-height: calc(100vh - #{$header-height-short});
                  width: 100%;
                }

                ul {
                  margin: 0;

                  li {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: center;
                    border-bottom: 1px solid $north-snow;
                    padding: 8px 16px;

                    &:last-child {
                      border-bottom: 0;
                    }

                    button {
                      &:focus {
                        outline: revert;
                      }
                      width: 100%;
                      background-color: transparent;
                      text-align: left;
                      -moz-appearance: auto;
                      -webkit-appearance: auto;
                      appearance: auto;
                    }

                    .title {
                      display: block;
                      font-style: normal;
                      font-weight: 500;
                      font-size: 14px;
                      line-height: 21px;
                      /* text (gray-scale)(gray 5) */
                      color: $text;
                    }

                    .address-wrapper {
                      margin-top: 4px;
                      display: flex;
                      align-items: center;

                      .address {
                        font-style: normal;
                        font-weight: normal;
                        font-size: 11px;
                        line-height: 16px;
                        /* chain (gray-scale)(gray 4) */
                        color: $text;
                      }
                    }
                  }
                }
              }
            }
          }

          button.header-button {
            &:focus {
              outline: revert;
            }
            background-color: $vacan;
            color: white;
            -moz-appearance: auto;
            -webkit-appearance: auto;
            appearance: auto;
          }

          div.search-place {
            min-width: 300px;
            margin: 0 16px;
            padding: 10px 0;
            background-color: white;
            position: sticky;
            top: 0;
            z-index: 1;

            label {
              display: block;
              width: 100%;
            }

            .inner {
              cursor: pointer;
              display: flex;
              border-bottom: 1px $knight inset;

              &:hover,
              &:focus-within {
                border-bottom: 1px $grape3-hover inset;

                i {
                  color: $grape3-hover;
                }
              }

              label {
                width: 100%;
              }

              input {
                box-shadow: 0 0;
                border: 0;
                width: 100%;

                &::placeholder {
                  color: $knight;
                }
              }

              .search-xs {
                font-size: 18px;
                color: $freeza;
                display: flex;
                align-items: center;
                margin-top: 4px;
                background-color: white;
              }
            }
          }

          div.header-notice {
            text-align: center;
            padding: 26px 0 13px;
            i {
              display: block;
              margin: 0 auto;
              width: 36px;
            }
          }

          div.network-error {
            color: $important;
            i {
              color: $important;
            }
          }

          div.search-not-found {
            color: $text;
            i {
              color: $text;
            }
          }

          div.search-error {
            text-align: center;
            color: $text;
          }

          div.has-more-place {
            margin: 0 16px;
            p {
              text-align: center;
              font-size: 12px;
              color: $text;
              padding: 1px 12px;
            }
          }
        }
      }
    }
  }
}
</style>
